import { Component, EventEmitter, Input, Output } from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { CustomFormValidators, FormValidationService } from "@app/directives/_forms/form-validation.service";
import { untilDestroyed } from "@ngneat/until-destroy";
import { ICameraConfiguration } from "../../_models/ICameraConfiguration";
import './config-editor.component.scss';

@Component({
    selector: 'config-editor',
    template: require('./config-editor.component.html')
})
export class ConfigEditorComponent {
    @Input() config: ICameraConfiguration;
    @Input() configs: ICameraConfiguration[];
    @Output() configSaved: EventEmitter<ICameraConfiguration> = new EventEmitter<ICameraConfiguration>();
    configData: any;
    configForm: FormGroup;
    formCategories: { title: string, formGroup: FormGroup }[];

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly formValidationService: FormValidationService) { }

    ngOnInit(): void {
        this.createFormCategories();
    }

    saveConfig(): void {
        if (!this.formValidationService.checkFormValid(this.configForm)) {
            return;
        }

        this.configForm.patchValue({ Name: this.ensureUniqueName(this.config.Name, this.configs) });

        let updatedConfigData: any = {};

        this.formCategories.forEach(formCategory => {
            updatedConfigData[formCategory.title] = {};
            Object.assign(updatedConfigData[formCategory.title], formCategory.formGroup.value);
        })

        const updatedConfigJsonString = JSON.stringify(updatedConfigData);

        this.config.Config = updatedConfigJsonString;

        this.configSaved.emit(this.config);
    }

    getControlType(control: FormControl): string {
        if (control.value instanceof Boolean || typeof control.value === 'boolean') {
            return 'boolean';
        } else if (typeof control.value === 'number') {
            return 'number';
        } else {
            return 'text';
        }
    }

    //--

    private createFormCategories(): void {
        this.formCategories = [];

        this.configData = JSON.parse(this.config.Config);

        for (const category in this.configData) {
            let formCategory = {
                title: category,
                formGroup: this.createFormGroup(category)
            }

            formCategory.formGroup.statusChanges.pipe(untilDestroyed(this)).subscribe(status => {
                this.updateNewConfig(status);
            })

            this.formCategories.push(formCategory);
        }
        this.formCategories.sort((a, b) => {
            if (a.title < b.title) {
                return -1;
            }
            if (a.title > b.title) {
                return 1;
            }
            return 0;
        });
        this.createStandardFormCategories();
    }

    private createFormGroup(categoryName: string): FormGroup {

        const formGroupConfig = {};

        for (const key in this.configData[categoryName]) {
            if (this.configData[categoryName].hasOwnProperty(key)) {
                formGroupConfig[key] = new FormControl(this.configData[categoryName][key]);
            }
        }

        return this.formBuilder.group(formGroupConfig);
    }

    private createStandardFormCategories(): void {
        this.configForm = new FormGroup({
            "Name": new FormControl(this.config.Name)
        })

        this.configForm.statusChanges.pipe(untilDestroyed(this))
            .subscribe(status => {
                this.updateNewConfig(status);
            })
    }

    private updateNewConfig(status: string): void {
        if (status == "INVALID") {
            return;
        }

        this.config = {
            ...this.config,
            Id: "", // If config has changed, save as new
            Name: this.configForm.get("Name").value
        }
    }

    private ensureUniqueName(name: string, existingNames: ICameraConfiguration[]): string {
        let uniqueName = name;
        let counter = 1;

        const nameSet = new Set(existingNames.map(config => config.Name));

        while (nameSet.has(uniqueName)) {
            uniqueName = `${name} (Copy ${counter})`;
            counter++;
        }

        return uniqueName;
    }
}
